import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import axios from 'axios'
import { cartClient } from '../utils/cart/cart'
import { extractProductsFromCart } from '../helpers/extractProductsFromCart'
import { useToast } from './ToastContext'
import useCustomer from './CustomerContext'
import trackMetaPixelEvent from '../utils/metaPixel/trackMetaPixelEvent'
import getCorrectedMarket from '../helpers/getCorrectedMarket'
import { gbpMarket, usdMarket } from '../constants/markets'
import { CARE_PRODUCT, hasCareEnterprise } from '../helpers/hasCareEnterprise'
import getCartUserMessage from '../helpers/cart/getCartUserMessage'
import getRentalProductAnalogsNote from '../helpers/getRentalProductAnalogsNote'
import { getOrphanRentalExtras } from '../helpers/rentalExtraOnly'
import { syncCartToKlaviyo } from '../utils/klaviyo/syncCartToKlaviyo'
import { trackCartCheckoutTiming } from '../utils/klaviyo/trackCartCheckoutTiming'
import { trackAddedToCart } from '../utils/klaviyo/klaviyoEvents'

const cartInitErrorToastKey = 'heliguy_cart_init_error_shown'

const defaultValues = {
  cart: null,
  cartItems: [],
  cartRentalItems: [],
  cartId: null,
  loading: false,
  currentMarket: 'GB',
  detectedCountry: 'GB',
  setCurrentMarket: () => {},
  setDetectedCountry: () => {},
  setCart: () => {},
  addVariantToCart: () => {},
  addVariantsListToCart: () => {},
  removeLineItem: () => {},
  clearCart: () => {},
  removeBuyerIdentity: () => {},
  onCheckout: () => {},
}

export const RENTAL_DEPOSIT_VARIANT_ID = 'gid://shopify/ProductVariant/43830498263274'
export const ASSIST_PRODUCT_VARIANT_ID = 'gid://shopify/ProductVariant/54914839118210'
export const CARE_BASIC_VARIANT_ID = 'gid://shopify/ProductVariant/56318759108994'
export const CARE_PLUS_VARIANT_ID = 'gid://shopify/ProductVariant/56318753505666'

const StoreContext = createContext(defaultValues)

const isBrowser = typeof window !== 'undefined'
const localStorageCartKey = 'shopify_cart'
const localStorageNeedToClearCartKey = 'shopify_need_to_clear_cart'
const localStorageCartItemsKey = 'shopify_cart_items'
const localStorageCartRentalItemsKey = 'shopify_cart_rental_items'

export function StoreProvider({ children }) {
  const [cart, setCart] = useState(defaultValues.cart)
  const [cartItems, setCartItems] = useState(defaultValues.cartItems)
  const [cartRentalItems, setCartRentalItems] = useState(defaultValues.cartItems)
  const [cartId, setCartId] = useState(defaultValues.cartId)
  const [loading, setLoading] = useState(false)
  const [currentMarket, setCurrentMarket] = useState('GB')
  const [detectedCountry, setDetectedCountry] = useState('GB')

  const { showToast } = useToast()
  const { accessToken, customer } = useCustomer()

  const syncKlaviyoIfLoggedIn = useCallback(
    (cartData, source) => {
      if (accessToken && customer?.email && cartData) {
        syncCartToKlaviyo(cartData, customer, source)
      }
    },
    [accessToken, customer]
  )

  const syncRentalProductAnalogsNote = useCallback(async (targetCartId, items) => {
    if (!targetCartId) return

    const note = getRentalProductAnalogsNote(items)

    try {
      await cartClient.updateCartNote({ cartId: targetCartId, note })
    } catch (error) {
      console.warn('Failed to update rental product analogs cart note:', error)
    }
  }, [])

  const setCartIdToStorage = useCallback((cartId) => {
    if (!cartId) return
    localStorage.setItem(localStorageCartKey, cartId)
  }, [])

  // rental:
  // 2. if removed last rental product than remove deposit
  // 3. what if we have a few rentals, what's with the deposit

  const updateBuyerIdentity = useCallback(
    async (token, customer, cartId) => {
      const buyerIdentity = {
        customerAccessToken: token,
        email: customer.email,
        phone: customer.phone,
        countryCode: getCorrectedMarket({ currentMarket, detectedCountry }) || 'GB',
      }

      const result = await cartClient.buyerIdentityUpdate(buyerIdentity, cartId)
      if (result?.error) {
        console.error('Error updating buyer identity:', result.error)
      }
    },
    [detectedCountry, currentMarket]
  )

  const combineWithActualCart = useCallback(
    (items) => {
      const updateItems = []
      const newItems = []

      items.forEach((item) => {
        const exist = cartItems.find((cartItem) => cartItem.product.id === item.merchandiseId)
        const isRental = !!item?.attributes?.find((attr) => attr.key === 'rental')

        if (exist && !isRental) {
          updateItems.push({
            ...item,
            quantity: (exist?.quantity || 0) + item.quantity,
            attributes: exist?.attributes || [],
            id: exist?.product?.cartLineId,
          })
        } else {
          newItems.push(item)
        }
      })

      return [updateItems, newItems]
    },
    [cartItems]
  )

  const removeBuyerIdentity = useCallback(async () => {
    if (!cartId) return

    const result = await cartClient.buyerIdentityUpdate(
      {
        customerAccessToken: null,
      },
      cartId
    )
    if (result?.error) {
      console.error('Error removing buyer identity:', result.error)
    }
  }, [cartId])

  const onCheckout = useCallback(async () => {
    setLoading(true)
    try {
      localStorage.setItem(localStorageNeedToClearCartKey, 'true')
      // localStorage.removeItem(localStorageCartKey)

      const identityResult = await cartClient.buyerIdentityUpdate(
        {
          countryCode: detectedCountry,
        },
        cartId
      )

      if (identityResult?.error) {
        console.warn('buyerIdentityUpdate before checkout failed:', identityResult.error)
      }

      const cart = await cartClient.getCart(cartId)

      const orphanRentalExtras = getOrphanRentalExtras(extractProductsFromCart(cart))

      if (orphanRentalExtras.length) {
        localStorage.removeItem(localStorageNeedToClearCartKey)
        showToast(
          'Checkout unavailable',
          `${orphanRentalExtras
            .map(({ product }) => product?.productTitle)
            .join(
              ', '
            )} can only be rented together with a drone. Please add a drone rental or remove these items.`
        )
        setLoading(false)
        return
      }

      await syncRentalProductAnalogsNote(cartId, extractProductsFromCart(cart))

      if (accessToken && customer?.email && cart) {
        trackCartCheckoutTiming(cart, customer)
      }

      if (!cart?.checkoutUrl) {
        showToast(
          'Checkout unavailable',
          getCartUserMessage(
            null,
            'We could not open checkout. Please refresh the page and try again.'
          )
        )
        setLoading(false)
        return
      }

      const linesObj = (cart.lines?.edges || [])?.reduce(
        (acc, item) => {
          const { node } = item
          const units = parseInt(node?.attributes.find((attr) => attr.key === 'Units')?.value)

          if (units && !isNaN(units)) {
            const newAttributes =
              node?.attributes?.map((attr) => ({
                ...attr,
                value: attr.key === 'Units' ? '1' : attr.value,
              })) || []
            for (let i = 0; i < units; i++) {
              acc.splitLines.push({
                merchandiseId: node.merchandise.id,
                quantity: node.quantity,
                attributes: [...newAttributes, { key: '_split_unique_id', value: uuidv4() }],
              })
            }
          } else {
            acc.splitLines.push({
              merchandiseId: node.merchandise.id,
              quantity: node.quantity,
              attributes: node.attributes,
            })
          }

          acc.lines.push({
            quantity: node.quantity,
            merchandiseId: node.merchandise.id,
            attributes: node.attributes || [],
          })

          acc.ids.push(node.id)

          return acc
        },
        { lines: [], ids: [], splitLines: [] }
      )

      localStorage.setItem(localStorageCartItemsKey, JSON.stringify(linesObj.lines.reverse()))

      await cartClient.removeLineItem(cartId, linesObj.ids)
      await cartClient.addLineItem(cartId, linesObj.splitLines.reverse())

      const a = document.createElement('a')
      a.href = cart?.checkoutUrl

      if (cart?.lines?.edges && cart?.lines?.edges.length > 0) {
        const checkoutItems = cart.lines.edges.map(({ node: { merchandise, quantity } }) => ({
          productTitle: merchandise?.product?.title,
          price: +merchandise?.price?.amount || 0,
          shopifyId: merchandise?.product?.id,
          brand: 'Heliguy',
          variantTitle: merchandise?.id,
          quantity,
        }))

        trackMetaPixelEvent({
          event: 'InitiateCheckout',
          items: checkoutItems,
          currency: cart.lines.edges[0]?.node?.merchandise?.price?.currencyCode,
        })
      }

      setLoading(false)

      a.click()
      a.remove()
    } catch (e) {
      console.error('Error during checkout:', e)
      showToast(
        'Checkout unavailable',
        getCartUserMessage(e, 'We could not open checkout. Please refresh the page and try again.')
      )
      setLoading(false)
    }
  }, [cartId, detectedCountry, showToast, accessToken, customer, syncRentalProductAnalogsNote])

  const getCart = useCallback(
    async (savedCartId) => {
      const cartData = await cartClient.getCart(savedCartId)
      setCart(cartData)
      setCartItems(extractProductsFromCart(cartData))
      setCartId(savedCartId)

      if (accessToken && customer) {
        updateBuyerIdentity(accessToken, customer, savedCartId)
      }
    },
    [isBrowser, accessToken, customer, updateBuyerIdentity, currentMarket]
  )

  const resolveCartCountryCode = useCallback(
    (market) =>
      getCorrectedMarket({
        currentMarket: market ?? currentMarket,
        detectedCountry,
      }) || 'GB',
    [currentMarket, detectedCountry]
  )

  const createCart = useCallback(
    async (lines, market) => {
      const countryCode = resolveCartCountryCode(market)
      const result = await cartClient.createCart(lines, countryCode)

      if (result?.error || !result?.data) {
        console.error('Error creating cart:', result?.error)
        showToast("Cart hasn't been updated", getCartUserMessage(result?.error))
        return
      }

      const cart = result.data
      setCart(cart)
      setCartId(cart?.id)
      setCartItems(extractProductsFromCart(cart))
      localStorage.setItem(localStorageCartKey, cart?.id)
      if (accessToken && customer) {
        updateBuyerIdentity(accessToken, customer, cart?.id)
      }
    },
    [isBrowser, accessToken, customer, updateBuyerIdentity, resolveCartCountryCode, showToast]
  )

  const showCartInitErrorToast = useCallback(
    (error) => {
      if (!isBrowser) return
      if (sessionStorage.getItem(cartInitErrorToastKey)) return

      sessionStorage.setItem(cartInitErrorToastKey, '1')
      showToast(
        "Couldn't load your cart",
        getCartUserMessage(
          error,
          "We couldn't restore your saved cart. Refresh the page or add items again."
        )
      )
    },
    [showToast]
  )

  const initializeCart = useCallback(
    async (lines) => {
      const savedCartId = isBrowser ? localStorage.getItem(localStorageCartKey) : null
      const storedMarket = isBrowser ? localStorage.getItem('currentMarket') : null
      const market = resolveCartCountryCode(storedMarket || currentMarket)
      const needToClear = isBrowser ? localStorage.getItem(localStorageNeedToClearCartKey) : null

      setLoading(true)

      try {
        if (savedCartId) {
          const localCart = await cartClient.getCart(savedCartId)

          if (!localCart) {
            await createCart(needToClear === 'true' ? [] : lines, market)
          } else {
            if (localCart?.buyerIdentity?.countryCode !== market) {
              const identityResult = await cartClient.buyerIdentityUpdate(
                {
                  countryCode: getCorrectedMarket({ currentMarket, detectedCountry }),
                },
                savedCartId
              )

              if (identityResult?.error) {
                console.warn(
                  'buyerIdentityUpdate during cart init failed; continuing with existing cart:',
                  identityResult.error
                )
              }
            }
            await getCart(savedCartId)
          }
        } else {
          await createCart(lines, market)
        }
      } catch (error) {
        console.error('Error initializing cart:', error)
        showCartInitErrorToast(error)
      }

      localStorage.removeItem(localStorageNeedToClearCartKey)

      setLoading(false)
    },
    [
      isBrowser,
      getCart,
      createCart,
      resolveCartCountryCode,
      currentMarket,
      detectedCountry,
      showCartInitErrorToast,
    ]
  )

  useEffect(() => {
    const cartRentalItems = localStorage.getItem(localStorageCartRentalItemsKey)
    setCartRentalItems(JSON.parse(cartRentalItems || '[]'))
  }, [])

  useEffect(() => {
    const cartItems = localStorage.getItem(localStorageCartItemsKey)

    const parsedCartItems = JSON.parse(cartItems) || []

    initializeCart(parsedCartItems)
  }, [initializeCart, currentMarket, detectedCountry])

  // useEffect(() => {
  //   const needToClear = localStorage.getItem(localStorageNeedToClearCartKey)

  //   if (needToClear === 'true') return

  //   initializeCart()
  // }, [initializeCart])

  // The rental deposit is added automatically, so it is not reported as an added product
  const withoutRentalDeposit = (lines) =>
    lines.filter(({ merchandiseId }) => merchandiseId !== RENTAL_DEPOSIT_VARIANT_ID)

  const addVariantsListToCart = async (products) => {
    setLoading(true)

    const lines = products
      .map((product, index) =>
        prepareForCart(
          product.product,
          product?.quantity,
          product?.isRentalMetafield,
          product?.weeksCount,
          product?.rentalData,
          product?.additionalData,
          index === 0
        )
      )
      .flat()

    const [update, insert] = combineWithActualCart(lines)

    const isProductsRequireCareBasic = products.some((product) =>
      hasCareEnterprise({ type: CARE_PRODUCT.Basic, shopifyId: product?.shopifyId })
    )

    const isProductsRequireCarePlus = products.some((product) =>
      hasCareEnterprise({ type: CARE_PRODUCT.Plus, shopifyId: product?.shopifyId })
    )

    // if (!cartItems.length) {
    if (
      !cartItems.find((item) => item?.product?.id === ASSIST_PRODUCT_VARIANT_ID) &&
      products.some((product) => product?.product?.productType === 'Drones')
    ) {
      const mainProduct = products?.find((product) => product?.additionalData?._id)
      const parentId = mainProduct?.additionalData?._id

      const attributes = []

      if (parentId) {
        attributes.push({ key: '_parent_id', value: parentId })
      }

      insert.push({
        merchandiseId: ASSIST_PRODUCT_VARIANT_ID,
        quantity: 1,
        attributes,
      })
    }

    if (isProductsRequireCareBasic || isProductsRequireCarePlus) {
      const careProduct = isProductsRequireCareBasic ? CARE_BASIC_VARIANT_ID : CARE_PLUS_VARIANT_ID

      if (insert.length > 0) {
        const mainProduct = products?.find((product) => product?.additionalData?._id)
        const parentId = mainProduct?.additionalData?._id

        const attributes = []

        if (parentId) {
          attributes.push({ key: '_parent_id', value: parentId })
        }

        insert.push({
          merchandiseId: careProduct,
          quantity: mainProduct.quantity,
          attributes,
        })
      }

      if (update.length > 0) {
        update.forEach((item) => {
          const parent_id = item?.attributes?.find((attr) => attr?.key === '_id')?.value
          if (parent_id) {
            const existCareProduct = cartItems.find((cartItem) =>
              cartItem.attributes.some((attr) => attr?.value === parent_id)
            )

            if (existCareProduct) {
              update.push({
                merchandiseId: existCareProduct?.product?.id,
                quantity: item?.quantity || 0,
                attributes: existCareProduct?.attributes || [],
                id: existCareProduct?.product?.cartLineId,
              })
            }
          }
        })
      }
    }
    // }

    const totalCount = products.reduce((total, curr) => {
      if (curr?.isRentalMetafield) {
        return total + 1
      }
      return total + curr.quantity
    }, 0)

    try {
      let cart = cartId
        ? await cartClient.addLineItem(cartId, insert)
        : await cartClient.createCart(lines)

      if (update.length) {
        cart = await cartClient.updateLineItem(cart.data.id, update)
      }

      const rentalProducts = insert.filter(
        (product) => product?.attributes?.find((obj) => obj.key === 'rental')?.value === 'true'
      )

      if (rentalProducts.length) {
        const rentalItems = [
          ...cartRentalItems,
          ...rentalProducts.map((product) => ({
            variantId: product?.merchandiseId,
          })),
        ].map((product, index) => ({
          ...product,
          id: index,
        }))
        setCartRentalItems(rentalItems)
        localStorage.setItem(localStorageCartRentalItemsKey, JSON.stringify(rentalItems))
      }

      setCartItems(extractProductsFromCart(cart.data))
      setCartIdToStorage(cart.data.id)
      setCartId(cart.data.id)
      setCart(cart.data)

      await syncRentalProductAnalogsNote(cart.data.id, extractProductsFromCart(cart.data))

      if (cart.error) {
        showToast("Cart hasn't been updated", cart.error)
      } else {
        syncKlaviyoIfLoggedIn(cart.data, 'StoreContext.addVariantsListToCart')
        trackAddedToCart(cart.data, withoutRentalDeposit(lines))
        showToast(
          'Cart has been updated',
          `${totalCount} item${totalCount > 1 ? 's' : ''} added to your cart`
        )
      }
    } catch (error) {
      console.error(`Error in addVariantsListToCart: ${error}`)
      showToast("Cart hasn't been updated", error?.message || 'Unhandled error')
    } finally {
      setLoading(false)
    }
  }

  const prepareForCart = (
    product,
    quantity,
    isRentalMetafield,
    weeksCount = 1,
    rentalData,
    additionalData,
    includeDeposit = false
  ) => {
    const variantId = product.variants[0]?.id || product.variants[0]?.shopifyId
    const parsedQuantity = parseInt(quantity, 10)

    const newCartItem = {
      merchandiseId: variantId,
      quantity: parsedQuantity || 1,
    }
    const rentalDepositItem = {
      merchandiseId: RENTAL_DEPOSIT_VARIANT_ID,
      quantity: 1,
    }

    const attributes = rentalData
      ? [
          { key: 'rental', value: 'true' },
          {
            key: 'From',
            value: rentalData.from,
          },
          {
            key: 'To',
            value: rentalData.to,
          },
          {
            key: 'Units',
            value: rentalData.units,
          },
          ...[
            rentalData?.rrp
              ? {
                  key: '_item_value',
                  value: rentalData.rrp,
                }
              : null,
          ],
          ...[
            rentalData?.flyerId
              ? {
                  key: '_flyerID',
                  value: rentalData.flyerId,
                }
              : null,
          ],
          {
            key: '_insurance',
            value: rentalData?.insurance || 'https://placehold.co/400x400',
          },
          ...[
            rentalData?.training
              ? {
                  key: '_training_additional',
                  value: rentalData?.training || 'https://placehold.co/400x400',
                }
              : null,
          ],
          {
            key: '_deposit_accepted',
            value: 'on',
          },
          ...[
            rentalData?.productAnalog
              ? {
                  key: 'product_analog',
                  value: rentalData.productAnalog,
                }
              : null,
          ],
        ].filter(Boolean)
      : []

    if (additionalData) {
      attributes.push(
        ...Object.entries(additionalData || {}).map(([k, v]) => ({
          key: k,
          value: v,
        }))
      )
    }

    if (cartId) {
      const lines = [{ ...newCartItem, attributes }]

      if (isRentalMetafield && includeDeposit && !cartRentalItems?.length) {
        lines.push(rentalDepositItem)
      }

      return lines
    }

    const lineItemsForCart = [
      ...cartItems.map((item) => ({
        merchandiseId: item.product.id,
        quantity: item.quantity,
        attributes: item.attributes,
      })),
      newCartItem,
    ]

    const linesWithRental = [...lineItemsForCart]

    if (isRentalMetafield && includeDeposit) linesWithRental.push(rentalDepositItem)

    return linesWithRental
  }

  const addVariantToCart = async (
    product,
    quantity,
    isRentalMetafield,
    weeksCount = 1,
    rentalData,
    additionalData
  ) => {
    setLoading(true)
    const lines = prepareForCart(
      product,
      quantity,
      isRentalMetafield,
      weeksCount,
      rentalData,
      additionalData
    )

    const [update, insert] = combineWithActualCart(lines)

    try {
      const variantId = insert[0].merchandiseId
      let cart = {}

      if (cartId) {
        cart = await cartClient.addLineItem(cartId, insert)
      } else {
        cart = await cartClient.createCart(insert)
      }

      if (update.length) {
        cart = await cartClient.updateLineItem(cart.data.id, update)
      }

      if (isRentalMetafield) {
        setCartRentalItems([...cartRentalItems, { variantId }])
        localStorage.setItem(
          localStorageCartRentalItemsKey,
          JSON.stringify([...cartRentalItems, { variantId }])
        )
      }

      setCartItems(extractProductsFromCart(cart.data))
      setCartIdToStorage(cart.data?.id)
      setCartId(cart.data?.id)
      setCart(cart.data)

      await syncRentalProductAnalogsNote(cart.data?.id, extractProductsFromCart(cart.data))

      if (cart.error) {
        showToast("Cart hasn't been updated", cart.error)
      } else {
        syncKlaviyoIfLoggedIn(cart.data, 'StoreContext.addVariantToCart')
        trackAddedToCart(cart.data, withoutRentalDeposit(lines))
        showToast('Cart has been updated', '1 item added to your cart')
      }
    } catch (error) {
      console.error(`Error in addVariantToCart: ${error}`)
      showToast("Cart hasn't been updated", error?.message || 'Unhandled error')
    } finally {
      setLoading(false)
    }
  }

  const removeLineItem = async (cartLineId) => {
    setLoading(true)

    try {
      const lines = [cartLineId]

      const depositItem = cartItems.find((item) => item.product.id === RENTAL_DEPOSIT_VARIANT_ID)

      if (cartLineId === depositItem?.product?.cartLineId && cartRentalItems.length > 0) {
        showToast('Error removing item', "You can't remove the deposit item")
        return
      }

      const cartItem = cartItems.find((item) => item.product.cartLineId === cartLineId)
      const cartItemVariantId = cartItem?.product?.id

      if (
        !!cartRentalItems.length &&
        depositItem?.product?.cartLineId &&
        cartRentalItems.find((item) => item.variantId === cartItemVariantId)
      ) {
        if (!cartItem?.attributes?.find((attribute) => attribute.key === '_parent_id')?.value) {
          if (cartRentalItems.length === 1) {
            lines.push(depositItem.product.cartLineId)
          }
        }
      }

      const removeResponse = await cartClient.removeLineItem(cartId, lines)

      setCartItems(extractProductsFromCart(removeResponse.data))
      setCart(removeResponse.data)

      await syncRentalProductAnalogsNote(cartId, extractProductsFromCart(removeResponse.data))

      const newRentalItems = cartRentalItems.filter((item) => item.variantId !== cartItemVariantId)
      setCartRentalItems(newRentalItems)
      localStorage.setItem(localStorageCartRentalItemsKey, JSON.stringify(newRentalItems))

      if (removeResponse.error) {
        showToast("Cart hasn't been updated", removeResponse.error)
      } else {
        syncKlaviyoIfLoggedIn(removeResponse.data, 'StoreContext.removeLineItem')
        showToast('Cart has been updated', '1 item removed from your cart')
      }
    } catch (error) {
      console.error(`Error in removeLineItem: ${error}`)
      showToast("Cart hasn't been updated", error?.message || 'Unhandled error')
    } finally {
      setLoading(false)
    }
  }

  const removeLineItems = async (cartLineIds, onSuccess, onError) => {
    setLoading(true)

    try {
      const lines = cartLineIds || []

      const depositItem = cartItems.find((item) => item.product.id === RENTAL_DEPOSIT_VARIANT_ID)

      if (
        lines.some((item) => item === depositItem?.product?.cartLineId) &&
        cartRentalItems.length > 0
      ) {
        showToast('Error removing item', "You can't remove the deposit item")
        onError?.()
        return
      }

      const updateLines = []

      for (const lineId of cartLineIds) {
        const cartItem = cartItems.find((item) => item.product.cartLineId === lineId)
        const cartItemVariantId = cartItem?.product?.id

        if (
          !!cartRentalItems.length &&
          depositItem?.product?.cartLineId &&
          cartRentalItems.find((item) => item.variantId === cartItemVariantId)
        ) {
          if (cartItem?.attributes?.find((attribute) => attribute.key === '_parent_id')?.value) {
            continue
          }

          if (lines.includes(depositItem.product.cartLineId)) {
            continue
          }

          if (cartRentalItems.length === 1) {
            lines.push(depositItem.product.cartLineId)
          }
        }
      }

      if (updateLines.length) {
        await cartClient.updateLineItem(cartId, updateLines)
      }

      const removeResponse = await cartClient.removeLineItem(cartId, lines)

      setCartItems(extractProductsFromCart(removeResponse.data))
      setCart(removeResponse.data)

      await syncRentalProductAnalogsNote(cartId, extractProductsFromCart(removeResponse.data))

      const cartItemVariantIds = cartItems
        .filter((item) => cartLineIds.includes(item.product.cartLineId))
        .map((item) => item.product.id)

      const rentalItemsToDelete = []
      cartItemVariantIds.forEach((id) => {
        const currentItems = rentalItemsToDelete.map((item) => item.id)
        const item = cartRentalItems.find(
          (item) => item.variantId === id && !currentItems.includes(item.id)
        )
        if (item) {
          rentalItemsToDelete.push(item)
        }
      })

      const newRentalItems = cartRentalItems.filter(
        (item) =>
          !rentalItemsToDelete.some((ri) => ri.id === item.id && ri.variantId === item.variantId)
      )
      setCartRentalItems(newRentalItems)
      localStorage.setItem(localStorageCartRentalItemsKey, JSON.stringify(newRentalItems))

      if (removeResponse.error) {
        showToast("Cart hasn't been updated", removeResponse.error)
        onError?.()
      } else {
        syncKlaviyoIfLoggedIn(removeResponse.data, 'StoreContext.removeLineItems')
        onSuccess?.()
        showToast(
          'Cart has been updated',
          `${cartLineIds?.length || 1} item removed from your cart`
        )
      }
    } catch (error) {
      showToast("Cart hasn't been updated", error?.message || 'Unhandled error')
      onError?.()
      console.error(`Error in removeLineItem: ${error}`)
    } finally {
      setLoading(false)
    }
  }

  const updateLineItem = async (newLineItems, onError) => {
    setLoading(true)
    try {
      const updatedResponse = await cartClient.updateLineItem(cartId, newLineItems)

      setCartItems(extractProductsFromCart(updatedResponse.data))
      setCart(updatedResponse.data)

      if (updatedResponse.error) {
        showToast("Cart hasn't been updated", updatedResponse.error)
        onError?.()
      } else {
        syncKlaviyoIfLoggedIn(updatedResponse.data, 'StoreContext.updateLineItem')
        showToast('Cart has been updated', '')
      }
    } catch (error) {
      showToast("Cart hasn't been updated", error?.message || 'Unhandled error')
      console.error(`Error in updateLineItem: ${error}`)
      onError?.()
    } finally {
      setLoading(false)
    }
  }

  const clearCart = useCallback(() => {
    if (!customer?.email) return

    setCart(defaultValues.cart)
    setCartId(defaultValues.cartId)
    setCartItems(defaultValues.cartItems)
    localStorage.removeItem(localStorageCartKey)
  }, [customer])

  useEffect(() => {
    const storedCurrentMarket = localStorage.getItem('currentMarket')
    if (storedCurrentMarket) {
      setCurrentMarket(storedCurrentMarket)
    }

    const getDetectedLocation = async () => {
      try {
        const response = await axios(`${process.env.GATSBY_NETLIFY_API_URL}/detect_location`).catch(
          () => {
            throw new Error('Faild to detect the location')
          }
        )

        const detectedLocation = response?.data?.country?.code

        if (!/^[A-Z]{2}$/.test(detectedLocation)) {
          throw new Error('Invalid country format')
        }

        setDetectedCountry(detectedLocation)

        if (!storedCurrentMarket) {
          if (gbpMarket.includes(detectedLocation)) {
            setCurrentMarket('GB')
            localStorage.setItem('currentMarket', 'GB')

            return
          }
          if (usdMarket.includes(detectedLocation)) {
            setCurrentMarket('US')
            localStorage.setItem('currentMarket', 'US')

            return
          }
          setCurrentMarket('INT')
          localStorage.setItem('currentMarket', 'INT')

          // FOR A TESTING WITH GB LOCATION
          // setCurrentMarket('GB')
          // localStorage.setItem('currentMarket', 'GB')
        }
      } catch (e) {
        console.error(e.message || 'Internal server error')
      }
    }

    getDetectedLocation()
  }, [])

  const contextValue = useMemo(
    () => ({
      ...defaultValues,
      addVariantToCart,
      addVariantsListToCart,
      removeLineItem,
      removeLineItems,
      updateLineItem,
      clearCart,
      cart,
      setCart,
      cartId,
      cartItems,
      onCheckout,
      lineItemsCount: cartItems.length,
      loading,
      removeBuyerIdentity,
      currentMarket,
      detectedCountry,
      setCurrentMarket,
      setDetectedCountry,
      getCart,
    }),
    [
      addVariantToCart,
      addVariantsListToCart,
      removeLineItem,
      removeLineItems,
      updateLineItem,
      clearCart,
      cart,
      cartId,
      cartItems,
      onCheckout,
      loading,
      removeBuyerIdentity,
      currentMarket,
      detectedCountry,
      getCart,
    ]
  )

  return <StoreContext.Provider value={contextValue}>{children}</StoreContext.Provider>
}

const useStore = () => {
  const context = useContext(StoreContext)

  if (context === undefined) {
    throw new Error('useStore must be used within StoreContext')
  }

  return context
}

export default useStore
